<!--This is where the advanced search filters lives-->

<template>
  <div class="tender-filters-layout">
    <header class="tender-filters-layout__header">
      <v-list class="py-0">
        <v-list-item class="px-2 ma-0">
          <v-list-item-title class="font-weight-bold subtitle-1">
            <span>Advanced Search</span>
            <sup class="tender-filters-layout__help-sup">
              <a
                href="https://docs.tendertrace.com/guide/user-documentation/searching-the-data/advanced-search"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Advanced search documentation"
              >
                <v-icon x-small color="white">mdi-help-circle-outline</v-icon>
              </a>
            </sup>
          </v-list-item-title>
          <v-btn
            icon
            aria-label="Close advanced search"
            @click.stop="hideFilter()"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-list-item>
      </v-list>
      <v-divider></v-divider>
    </header>

    <div class="tender-filters-layout__scroll sidebarExpander v-label">
      <v-expansion-panels v-model="filterOpenPanels" multiple>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(0) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Keywords</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-combobox dense v-model="keyword_chips" x-small-chips deletable-chips clearable
              label="Enter tab separated keywords" :items="supp_smart_tag_items" item-text="key"
              :search-input.sync="supp_smart_tags" multiple x-small ref="keywords"
              @keydown.enter.prevent="addKeywordChip({ key: supp_smart_tags })"
              @keydown.tab.prevent="addKeywordChip({ key: supp_smart_tags })" @paste.native="onKeywordsPaste">
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" x-small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_kw(item)">{{ item.key }}</v-chip>
              </template>
            </v-combobox>
            <v-row class="pa-0 ma-0">
              <v-col class="pa-0 ma-0">
                <v-subheader class="pl-0 ml-0 py-0 my-0">
                  <v-tooltip max-width="290" right dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-switch v-model="and_query" label="Contains all keywords"></v-switch>
                      </span>
                    </template>
                    <span>This setting will use an 'AND' condition to return only notices that contain ALL keywords.
                    </span>
                  </v-tooltip>
                </v-subheader>
              </v-col>
            </v-row>
            <v-row class="pa-0 ma-0">
              <v-col class="pa-0 ma-0">
                <v-subheader class="pl-0 ml-0 py-0 my-0">
                  <v-tooltip max-width="290" right dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-switch v-model="exclude_panel" label="Exclude panel notices"></v-switch>
                      </span>
                    </template>
                    <span>This setting will remove all notices awarded via panels or standing offers from the
                      results.</span>
                  </v-tooltip>
                </v-subheader>
              </v-col>
            </v-row>
            <v-row class="pt-0 pb-0 ma-0">
              <v-col class="pa-0 ma-0">
                <v-subheader class="pl-0 ml-0 py-0 my-0">
                  <v-tooltip max-width="290" right dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-switch v-model="isparent" label="Amended notices only"></v-switch>
                      </span>
                    </template>
                    <span>This setting will only return federal government notices. States and territories at this point
                      in
                      time do not clearly publish amendments.</span>
                  </v-tooltip>
                </v-subheader>
              </v-col>
            </v-row>
            <v-row class="pa-0 ma-0">
              <v-col class="pa-0 ma-0">
                <v-subheader class="pl-0 ml-0 py-0 my-0">
                  <v-tooltip max-width="290" right dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-switch v-model="exclude_grant" label="Exclude grants"></v-switch>
                      </span>
                    </template>
                    <span>This setting will remove all grant and grant opportunities from the results.</span>
                  </v-tooltip>
                </v-subheader>
              </v-col>
            </v-row>
            <v-tooltip max-width="290" right dark color="#000000" style="opacity: 0.7;">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-subheader v-if="keyword_chips.length > 0" class="pl-0 ml-0 py-0 my-0">
                    <v-switch v-model="supplier_description" label="Search supplier description"></v-switch>
                  </v-subheader>
                </span>
              </template>
              <span>Include Tendertrace's enriched supplier descriptions in the keyword search. This can help reveal
                contracts that would otherwise not appear due to insufficient data being published by the public
                entity.</span>
            </v-tooltip>
            <v-tooltip max-width="290" right dark color="#000000" style="opacity: 0.7;">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-subheader v-if="keyword_chips.length > 0" class="pl-0 ml-0 py-0 my-0">
                    <v-switch v-model="supplier_smart_tags" label="Include supplier smart tags"></v-switch>
                  </v-subheader>
                </span>
              </template>
              <span>Supplier smart tags are tags extracted from the supplier's web profile (websites, news, blogs,
                marketing
                etc). Smart tags can help significantly expand your search for contracts, however they may also return
                unrelated results.</span>
            </v-tooltip>

            <v-tooltip max-width="290" right dark color="#000000" style="opacity: 0.7;">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-subheader v-if="keyword_chips.length > 0" class="pl-0 ml-0 py-0 my-0">
                    <v-switch v-model="panel_metadata" label="Include panel metadata in search"></v-switch>
                  </v-subheader>

                </span>
              </template>
              <span>Include metadata for panels in the search, including panel title, description, and lead
                agency.</span>
            </v-tooltip>

            <v-combobox v-model="request_chips" x-small-chips clearable label="Notice Reference ID(s)" multiple x-small>
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_request(item)">{{ item }}</v-chip>
              </template>
            </v-combobox>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(1) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Panels / Standing
            Offers</v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row class="pa-0 ma-0">
              <v-col class="pa-0 ma-0">
                <v-subheader class="pl-0 ml-0 py-0 my-0">
                  <v-tooltip max-width="290" right dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-switch v-model="ispanel" label="Panel/Standing Offer results only"></v-switch>
                      </span>
                    </template>
                    <span>This setting will only return federal government notices. States and territories at this point
                      in
                      time do not clearly publish amendments.</span>
                  </v-tooltip>
                </v-subheader>
              </v-col>
            </v-row>
            <v-row class="pa-0 ma-0">
              <v-col class="pa-0 ma-0">
                <v-combobox v-model="panel_chips" x-small-chips clearable label="Panel/Standing Offer ID(s)" multiple
                  x-small>
                  <template v-slot:selection="{ attrs, item, select, selected }">
                    <v-chip color="rgb(255, 20, 99)" small v-bind="attrs" :input-value="selected" close label
                      @click="select" @click:close="remove_panel(item)">{{ item }}</v-chip>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(2) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Timeframe</v-expansion-panel-header>

          <v-expansion-panel-content>
            <TimeframePicker
              :key="'contract-start-' + timeframeResetKey"
              v-model="contractStartTimeframe"
              label="Include results with start dates only"
              @input="syncTimeframe('contract_start', $event)"
            />
            <v-divider class="my-2" />
            <TimeframePicker
              :key="'contract-end-' + timeframeResetKey"
              v-model="contractEndTimeframe"
              label="Include results with expiry dates only"
              @input="syncTimeframe('contract_end', $event)"
            />
            <v-divider class="my-2" />
            <TimeframePicker
              :key="'published-' + timeframeResetKey"
              v-model="publishedTimeframe"
              label="Include results with published dates"
              tooltip="Use this option to filter <b>Federal (FED)</b> government contracts and requests <b>ONLY</b>. State and Territory governments are not consistent in reporting published dates of contracts which will cause discrepancies in your results."
              @input="syncTimeframe('published', $event)"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(3) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Category</v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-row class="pt-0 pb-4 ma-0">
              <v-col class="pa-0 ma-0">
                <v-subheader class="pl-0 ml-0 py-0 my-0">
                  <v-switch v-model="isconsultancy" label="Classified as Consultancy"></v-switch>
                </v-subheader>
              </v-col>
            </v-row>
            <v-tooltip top max-width="400" class="pa-0 ma-0">
              <template v-slot:activator="{ on, attrs }">
                <v-combobox v-model="selected_parent_categories" x-small-chips :items="parent_category_options"
                  item-text="name" clearable dense label="Major category" multiple x-small
                  :menu-props="{ maxWidth: '350', maxHeight: '300' }" ref="parent_categories" v-bind="attrs" v-on="on">
                  <template v-slot:selection="{ attrs, item, select, selected }">
                    <v-chip color="rgb(255, 20, 99)" x-small v-bind="attrs" :input-value="selected" close label
                      @click="select" @click:close="remove_major_category(item)"><span class="chip-label">{{ item.name
                      }}</span></v-chip>
                  </template>
                </v-combobox>
              </template>
              <span>
                <div class="body-1 font-weight-bold">A note about filtering by major categories</div>
                <div class="caption pt-1">The Major Category filter <b>will only work</b> for contracts that have been
                  tagged with the correct major category based on the <b>UNSPC standard</b>. In general, <b>only the
                    Federal
                    government</b> uses the UNSPC standard consistently. This means that using this filter will possibly
                  result in a subset of the actual results being returned. </div>
                <div class="caption pt-1">While useful, if your search extends beyond only Federal contracts, you may
                  also
                  want to run a separate search using on the category filter below to compare results.</div>
              </span>
            </v-tooltip>

            <v-tooltip top max-width="400" class="pa-0 ma-0">
              <template v-slot:activator="{ on, attrs }">
                <v-combobox v-model="selected_category_list" :items="category_list" :loading="loadingCategoryLists"
                  :search-input.sync="search_category_lists" label="Category" deletable-chips x-small-chips dense
                  x-small clearable multiple v-bind="attrs" v-on="on"
                  :menu-props="{ maxWidth: '350', maxHeight: '300' }">
                  <template v-slot:selection="{ attrs, item, select, selected }">
                    <v-chip color="rgb(255, 20, 99)" label small v-bind="attrs" :input-value="selected" close
                      @click="select" @click:close="remove_category_list(item)">
                      <span class="chip-label">{{ item }}</span></v-chip>
                  </template>
                </v-combobox>
              </template>
              <span>
                <div class="body-2">Start typing to select a category. Press ENTER to select the keyword you have typed
                </div>
              </span>
            </v-tooltip>

            <v-tooltip top max-width="400" class="pa-0 ma-0">
              <template v-slot:activator="{ on, attrs }">
                <v-combobox v-model="selected_exclude_category_list" :items="category_list"
                  :loading="loadingCategoryLists" :search-input.sync="search_exclude_category_lists"
                  label="Exclude category" deletable-chips x-small-chips dense x-small clearable multiple v-bind="attrs"
                  v-on="on" :menu-props="{ maxWidth: '350', maxHeight: '300' }">
                  <template v-slot:selection="{ attrs, item, select, selected }">
                    <v-chip color="rgb(255, 20, 99)" label small v-bind="attrs" :input-value="selected" close
                      @click="select" @click:close="remove_exclude_category_list(item)">
                      <span class="chip-label">{{ item }}</span></v-chip>
                  </template>
                </v-combobox>
              </template>
              <span>
                <div class="body-2">Exclude notices in these categories from the results.</div>
              </span>
            </v-tooltip>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(4) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Buyer</v-expansion-panel-header>
          <v-expansion-panel-content>
            <cluster-list :entityType="'publisher'" :injectedClusters="injectPubClusters" :entityIDType="'publisher_id'"
              @updateCluster="updatePubCluster" />
            <v-menu v-model="industry_tree_open" :close-on-content-click="false" offset-y
              :nudge-top="8" content-class="industry-tree-menu" class="d-block mb-2">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="industry-tree-activator">
                  <v-autocomplete :value="selected_publisher_industry" :items="selected_publisher_industry"
                    label="Industry sector" multiple dense x-small-chips clearable hide-details
                    :loading="loadingIndustries" :search-input.sync="industry_tree_search"
                    :menu-props="{ value: false }" item-text="name" item-value="id"
                    :append-icon="industry_tree_open ? 'mdi-menu-up' : 'mdi-menu-down'"
                    @click:clear.stop="selected_industry_ids = []; industry_tree_open = false">
                    <template v-slot:selection="{ item }">
                      <v-chip color="rgb(255, 20, 99)" small label close
                        @click.native.stop @click:close="remove_buyer_industry(item)">
                        <span class="chip-label">{{ item.name || item }}</span>
                      </v-chip>
                    </template>
                  </v-autocomplete>
                </div>
              </template>
              <v-card flat color="white">
                <v-card-text class="pa-2">
                  <div class="industry-tree-scroll">
                    <v-treeview v-model="selected_industry_ids" :items="cofog_classifications"
                      :search="industry_tree_search" item-key="id" item-text="name" item-children="children"
                      selectable selection-type="leaf" dense color="rgb(255, 20, 99)" hoverable>
                      <template v-slot:label="{ item }">
                        <span class="caption">{{ parseFloat(item.code) }} – {{ item.name }}</span>
                      </template>
                    </v-treeview>
                  </div>
                </v-card-text>
              </v-card>
            </v-menu>

            <v-combobox dense deletable-chips v-model="agency_name_chips" x-small-chips :items="pub_items"
              item-text="key" :search-input.sync="pub_search" clearable label="Include these entity names" multiple class="mt-4"
              x-small :menu-props="{ maxWidth: '350', maxHeight: '300' }"
              @keydown.enter.prevent="addBuyerChip({ key: pub_search })"
              @keydown.tab.prevent="addBuyerChip({ key: pub_search })">
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_agency_name(item)">
                  <span class="chip-label">{{ item.key }}</span></v-chip>
              </template>
            </v-combobox>
            <v-combobox dense v-model="agency_name_chips_exclude" x-small-chips deletable-chips
              :items="exclude_pub_items" item-text="key" :search-input.sync="exclude_pub_search" clearable
              label="Exclude these entity names" multiple x-small :menu-props="{ maxWidth: '350', maxHeight: '300' }"
              @keydown.enter.prevent="addExcludeBuyerChip({ key: exclude_pub_search })"
              @keydown.tab.prevent="addExcludeBuyerChip({ key: exclude_pub_search })">
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_excluded_agency_name(item)">
                  <span class="chip-label">{{ item.key }}</span></v-chip>
              </template>
            </v-combobox>

            <v-combobox dense class="pt-1" v-model="tendle_pub_id_chips" :items="tendle_pub_id_items" x-small-chips
              deletable-chips clearable label="Enter one or more tendertrace IDs" multiple x-small>
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_tendle_pub_id(item)">{{ item }}</v-chip>
              </template>
            </v-combobox>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(5) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Supplier</v-expansion-panel-header>

          <v-expansion-panel-content>
            <cluster-list :injectedClusters="injectSuppClusters" :entityType="'supplier'" :entityIDType="'supplier_id'"
              @updateCluster="updateSuppCluster" />
            <v-combobox dense v-model="supplier_industry_chips" x-small-chips deletable-chips
              :items="supplierIndustryOptions" item-text="text" item-value="value" clearable label="Industry" multiple
              x-small :menu-props="{ maxWidth: '350', maxHeight: '300' }">
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip v-on="on" color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_supp_industry(item)"><span class="chip-label">{{ item.text
                  }}</span></v-chip>
              </template>
            </v-combobox>
            <v-combobox dense v-model="supplier_name_chips" x-small-chips deletable-chips :items="supp_items"
              item-text="key" :search-input.sync="supp_search" clearable label="Include these entity names" multiple
              x-small :menu-props="{ maxWidth: '350', maxHeight: '300' }"
              @keydown.enter.prevent="addSupplierChip({ key: supp_search })"
              @keydown.tab.prevent="addSupplierChip({ key: supp_search })">
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip v-on="on" color="rgb(255, 20, 99)" small v-bind="attrs" label :input-value="selected" close
                  @click="select" @click:close="remove_supp_name(item)"><span class="chip-label">{{ item.key
                  }}</span></v-chip>
              </template>
            </v-combobox>
            <v-combobox dense v-model="supplier_group_name_chips" x-small-chips deletable-chips
              :items="supplier_group_items" item-text="key" :search-input.sync="supplier_group_search" clearable
              label="Include these supplier group names" multiple x-small
              :menu-props="{ maxWidth: '350', maxHeight: '300' }"
              @keydown.enter.prevent="addSupplierGroupChip({ key: supplier_group_search })"
              @keydown.tab.prevent="addSupplierGroupChip({ key: supplier_group_search })">
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small v-bind="attrs" label :input-value="selected" close
                  @click="select" @click:close="remove_supplier_group_name(item)">
                  <span class="chip-label">{{ item.key }}</span></v-chip>
              </template>
            </v-combobox>
            <v-combobox dense v-model="supplier_name_chips_exclude" x-small-chips deletable-chips
              :items="exclude_supp_items" item-text="key" :search-input.sync="exclude_supp_search" clearable
              label="Exclude these entity names" multiple x-small :menu-props="{ maxWidth: '350', maxHeight: '300' }"
              @keydown.enter.prevent="addSupplierExcludeChip({ key: exclude_supp_search })"
              @keydown.tab.prevent="addSupplierExcludeChip({ key: exclude_supp_search })">
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_excluded_supp_name(item)">
                  <span class="chip-label">{{ item.key }}</span></v-chip>
              </template>
            </v-combobox>
            <v-combobox v-model="supplier_id_chips" x-small-chips clearable label="Enter supplier ABN(s)" multiple
              x-small>
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_supp_id(item)">{{ item }}</v-chip>
              </template>
            </v-combobox>
            <v-combobox class="pt-1" v-model="tendle_supp_id_chips" :items="tendle_supp_id_items" x-small-chips
              deletable-chips clearable label="Enter one or more tendertrace IDs" multiple x-small>
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_tendle_supp_id(item)">{{ item }}</v-chip>
              </template>
            </v-combobox>
            <v-row class="pa-0 ma-0">
              <v-col class="pa-0 ma-0">
                <v-subheader class="pl-0 ml-0 py-0 my-0">
                  <v-tooltip max-width="290" right dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-switch v-model="isindigenous" label="Indigenous suppliers only"></v-switch>
                      </span>
                    </template>
                    <span>Use this filter to return only notices that are awarded to Indigenous suppliers.
                    </span>
                  </v-tooltip>
                </v-subheader>
              </v-col>
            </v-row>

          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(6) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Award Amount</v-expansion-panel-header>

          <v-expansion-panel-content>
            <div class="sidebarTopSubmit">
              <v-row>
                <v-col class="pt-0">
                  <v-text-field clearable v-model="amount_min" label="Min"></v-text-field>
                </v-col>
                <v-col class="pt-0">
                  <v-text-field clearable v-model="amount_max" label="Max"></v-text-field>
                </v-col>
              </v-row>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(7) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Type of Notice</v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-tooltip top max-width="400" class="pa-0 ma-0">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <div class="v-label">
                    <ul style="list-style: none; padding-left:0;">
                      <li v-for="item in type_options" :key="item.value">
                        <v-checkbox :label="item.name" color="rgb(255, 20, 99)" :value="item.value" hide-details
                          v-model="selected_types"></v-checkbox>
                      </li>
                    </ul>
                  </div>
                </span>
              </template>
              <span>
                <div class="body-1 font-weight-bold">A note about Type filters</div>
                <div class="caption pt-1">The <b>Grant</b>, <b>Grant Opportunity</b> and <b>Planned Procurement</b>
                  filters
                  <b>will likely only</b> return federal government data. This is due to the inconsistent nature and
                  availability of state and territory grant and planned procurement data.
                </div>
              </span>
            </v-tooltip>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(8) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Government</v-expansion-panel-header>

          <v-expansion-panel-content>
            <div class="v-label">
              <ul style="list-style: none; padding-left:0;">
                <li v-for="item in territory_options" :key="item.value">
                  <v-checkbox :loading="loadingGovts" :label="item.value" color="rgb(255, 20, 99)" :value="item.value"
                    hide-details v-model="selected_territories"></v-checkbox>
                </li>
              </ul>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(9) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Procurement
            Method</v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-combobox v-model="selected_procurement_method" :items="procurement_method_list"
              :loading="loadingProcurementMethods" :search-input.sync="search_procurement_methods"
              label="Select one or more options" deletable-chips x-small-chips dense x-small clearable multiple
              :menu-props="{ maxWidth: '350', maxHeight: '300' }">
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_procurement_method(item)">
                  <span class="chip-label">{{ item }}</span></v-chip>
              </template>
            </v-combobox>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel
          :class="{ 'tender-filters__panel--highlighted': isFilterPanelHighlighted(10) }"
          style="background-color:#12253a;"
        >
          <v-expansion-panel-header class="font-weight-light subtitle-2 py-0 tender-filters__panel-header">Procurement
            Contact</v-expansion-panel-header>

          <v-expansion-panel-content>
            <v-combobox v-model="selected_contact" :items="contact_list" :loading="loadingContacts"
              :search-input.sync="search_contacts" label="Start typing a contact name" deletable-chips item-text="name"
              x-small-chips dense x-small clearable multiple :menu-props="{ maxWidth: '350', maxHeight: '300' }">
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip color="rgb(255, 20, 99)" small label v-bind="attrs" :input-value="selected" close
                  @click="select" @click:close="remove_contact(item)">
                  <span class="chip-label">{{ item.name }}</span></v-chip>
              </template>
            </v-combobox>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <footer class="tender-filters-layout__footer sidebarTopSubmit" align="center">
      <v-btn
        class="text-capitalize body-2 desktop-nav"
        color="white"
        outlined
        small
        width="80%"
        light
        @click="submitSearch()"
      >
        Run Search
      </v-btn>
      <v-btn
        class="text-capitalize body-2 mobile-nav"
        color="white"
        outlined
        small
        light
        width="80%"
        @click="submitSearch()"
      >
        Run Search
      </v-btn>
      <div>
        <v-btn
          text
          class="caption text-capitalize"
          color="#2c9dd5"
          @click="reset_filters()"
        >
          Reset Filters
        </v-btn>
      </div>
    </footer>

    <v-dialog v-model="major_category_disclaimer" max-width="500px">
      <div>
        <v-alert v-model="major_category_disclaimer" :border="'left'" close-text="Close Alert" color="white accent-4"
          dismissible class="mb-0 body-2">
          <v-card flat class="pa-0">
            <v-card-subtitle class="body-1 font-weight-bold">A note about filtering by major
              categories</v-card-subtitle>
            <v-card-text class="caption pt-0">The Major Category filter will only work for contracts that have been
              tagged
              with the correct major category based on the UNSPC standard. In general, only the Federal government uses
              the
              UNSPC standard consistently. This means that using this filter will likely only result in Federal
              government
              contracts being returned. </v-card-text>
            <v-card-text class="caption pt-0">While useful, you may also want to run a separate search using on the
              category
              keyword filters to compare results.</v-card-text>

          </v-card>

        </v-alert>
      </div>

    </v-dialog>
  </div>
</template>

<script>
import { autocomplete, getSubCategories, fetchBuyerIndustries, fetchCategoryList, getParentCategories, fetchProcurementMethods, fetchGovTypes, fetchSearchQuery, fetchAllContacts, fetchIndustries, fetchCofogClassifications } from "./APIManager.js";
import json from './data/categories.json';
import ClusterList from "./ClusterList.vue";
import TimeframePicker from "./TimeframePicker.vue";
import {
  computeTimeframeDates,
  createDefaultTimeframeState,
  createTimeframeStateFromDates
} from "../utils/timeframe";
import { splitKeywordTokens } from "../utils/tenderSearchKeywords";
import { getInstance } from "../auth";

export default {
  components: {
    ClusterList,
    TimeframePicker,
  },
  props: {
    source: String,
    mini: Boolean,
    /** When false, route changes do not reset/repopulate the form (for global drawer). */
    syncRouteOnNavigate: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      filterOpenPanels: [],
      disclaimer_msg_count: 0,
      major_category_disclaimer: false,
      ispanel: false,
      exclude_panel: false,
      and_query: false,
      exclude_grant: false,
      isconsultancy: false,
      isparent: false,
      isindigenous: false,
      supplier_smart_tags: false,
      supplier_description: false,
      panel_metadata: false,
      injectSuppClusters: [],
      injectPubClusters: [],
      search_industries: "",
      publisher_industry_list: [],
      cofog_classifications: [],
      cofog_flat_map: {},
      selected_industry_ids: [],
      industry_tree_open: false,
      industry_tree_search: "",
      selected_publisher_industry: [],
      selected_procurement_method: [],
      selected_contact: [],
      selected_category_list: [],
      selected_exclude_category_list: [],
      procurement_method_list: [],
      contact_list: [],
      category_list: [],
      search_procurement_methods: "",
      search_contacts: "",
      search_category_lists: "",
      search_exclude_category_lists: "",
      loadingProcurementMethods: Boolean(),
      loadingContacts: Boolean(),
      loadingCategoryLists: Boolean(),
      loadingIndustries: Boolean(),
      loadingGovts: Boolean(),
      selected_months: [],
      timeframeResetKey: 0,
      contractStartTimeframe: createDefaultTimeframeState(),
      contractEndTimeframe: createDefaultTimeframeState(),
      publishedTimeframe: createDefaultTimeframeState(),
      showMenu: false,
      contract_month_start: { name: "July", date: new Date(2005, 6).toISOString().substr(0, 10) },
      contract_month_end: { name: "June", date: new Date(2030, 5, 30).toISOString().substr(0, 10) },
      published_month_start: { name: "July", date: new Date(2005, 6).toISOString().substr(0, 10) },
      published_month_end: { name: "June", date: new Date(2030, 5, 30).toISOString().substr(0, 10) },
      expiry_month_start: { name: "July", date: new Date(2005, 6).toISOString().substr(0, 10) },
      expiry_month_end: { name: "June", date: new Date(2030, 5, 30).toISOString().substr(0, 10) },
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ],
      exclude_publisher: false,
      exclude_supplier: false,
      selected_supp_clusters: [],
      selected_pub_clusters: [],
      contract_start_enabled: false,
      contract_end_enabled: false,
      published_date_enabled: false,
      years: [2005, this.year() + 10],
      year_min: 1900,
      year_max: 2099,
      end_years: [2005, this.year() + 10],
      end_year_min: 1900,
      end_year_max: 2099,
      pub_years: [2005, this.year() + 10],
      pub_year_min: 1900,
      pub_year_max: 2099,
      cat_search: "",
      pub_search: "",
      exclude_pub_search: "",
      supp_search: "",
      exclude_supp_search: "",
      supp_smart_tags: "",
      supp_smart_tag_items: [],
      cat_items: [],
      pub_items: [],
      exclude_pub_items: [],
      supp_items: [],
      supplierIndustryOptions: [],
      industry_options: [],
      industry_options_formatted: [],
      pending_supplier_industries: [],
      supplier_industry_chips: [],
      supplier_group_name_chips: [],
      supplier_group_items: [],
      supplier_group_search: "",
      exclude_supp_items: [],
      isLoading: false,
      search_params: {},
      published_range: [],
      published_to: "",
      published_from: "",
      closing_range: [],
      closing_to: "",
      closing_from: "",
      contract_start_range: [],

      contract_end_range: [],
      contract_start_from: "",
      contract_start_to: "",
      contract_end_from: "",
      contract_end_to: "",
      keyword_chips: [],
      keyword_items: [],
      curr_date: new Date().toISOString().substr(0, 10),
      contractStartMenu: false,
      contractEndMenu: false,
      modal: false,
      publishedDateMenu: false,
      closingDateMenu: false,
      amount_index: null,
      amount_min: null,
      amount_max: null,
      custom_amount: false,
      supplier_id_chips: [],
      supplier_id_items: [],
      tendle_cat_id_chips: [],
      tendle_cat_id_items: [],
      tendle_supp_id_chips: [],
      tendle_supp_id_items: [],
      tendle_pub_id_chips: [],
      tendle_pub_id_items: [],
      supplier_name_chips: [],
      supplier_name_items: [],
      agency_name_chips: [],
      agency_name_chips_exclude: [],
      supplier_name_chips_exclude: [],
      agency_name_items: [],
      category_chips: [],
      category_items: [],
      request_chips: [],
      panel_chips: [],
      request_items: [],
      selected_territories: [],
      injectExcludeGovernment: [],
      parent_category_options: [],
      selected_parent_categories: [],
      territory_options: [],
      tenderFiltersInitialized: false,
      selected_statuses: [],
      status_options: [
        { value: "Current" },
        { value: "Expiring Soon" },
        { value: "Expired" },
        { value: "Unknown" }
      ],
      selected_types: [],
      type_options: [{ name: "Contract Notice", value: "Contract Notice" }, { name: "Request / Approach To Market", value: "Request" }, { name: "Planned Procurement", value: "Planned" }, { name: "Grant", value: "Grant" }, { name: "Grant Opportunity", value: "Grant Opportunity" }],
      predefined_amount: [
        { min: 0, max: 1000000 },
        { min: 1000001, max: 5000000 },
        { min: 5000001, max: 20000000 },
        { min: 20000001, max: 100000000 },
        { min: 100000001, max: 1000000000 }
      ]
    };
  },

  methods: {

    hasFilterItems(list) {
      return Array.isArray(list) && list.length > 0;
    },

    hasKeywordChips() {
      return (this.keyword_chips || []).some((chip) => chip && String(chip.key || '').trim());
    },

    isFilterPanelHighlighted(panelIndex) {
      switch (panelIndex) {
        case 0:
          return this.hasKeywordChips();
        case 1:
          return this.ispanel || this.hasFilterItems(this.panel_chips);
        case 2:
          return (
            this.contract_start_enabled ||
            this.contract_end_enabled ||
            this.published_date_enabled ||
            this.hasFilterItems(this.closing_range) ||
            this.hasFilterItems(this.published_range) ||
            this.hasFilterItems(this.contract_start_range) ||
            this.hasFilterItems(this.contract_end_range) ||
            this.hasFilterItems(this.selected_months)
          );
        case 3:
          return (
            this.isconsultancy ||
            this.hasFilterItems(this.selected_parent_categories) ||
            this.hasFilterItems(this.selected_category_list) ||
            this.hasFilterItems(this.selected_exclude_category_list) ||
            this.hasFilterItems(this.category_chips) ||
            this.hasFilterItems(this.tendle_cat_id_chips)
          );
        case 4:
          return (
            this.hasFilterItems(this.selected_pub_clusters) ||
            this.hasFilterItems(this.selected_publisher_industry) ||
            this.hasFilterItems(this.selected_industry_ids) ||
            this.hasFilterItems(this.agency_name_chips) ||
            this.hasFilterItems(this.agency_name_chips_exclude) ||
            this.hasFilterItems(this.tendle_pub_id_chips)
          );
        case 5:
          return (
            this.isindigenous ||
            this.hasFilterItems(this.selected_supp_clusters) ||
            this.hasFilterItems(this.supplier_industry_chips) ||
            this.hasFilterItems(this.supplier_name_chips) ||
            this.hasFilterItems(this.supplier_group_name_chips) ||
            this.hasFilterItems(this.supplier_name_chips_exclude) ||
            this.hasFilterItems(this.supplier_id_chips) ||
            this.hasFilterItems(this.tendle_supp_id_chips)
          );
        case 6:
          return (this.amount_min != null && this.amount_min > 0)
            || (this.amount_max != null && this.amount_max > 0);
        case 7:
          return this.hasFilterItems(this.selected_types)
            || this.hasFilterItems(this.selected_statuses);
        case 8:
          return this.hasFilterItems(this.selected_territories);
        case 9:
          return this.hasFilterItems(this.selected_procurement_method);
        case 10:
          return this.hasFilterItems(this.selected_contact);
        default:
          return false;
      }
    },

    getAdvancedFilterCount() {
      let count = 0;

      if (
        this.and_query ||
        this.exclude_panel ||
        this.exclude_grant ||
        this.isparent ||
        this.isindigenous ||
        this.isconsultancy ||
        this.supplier_smart_tags ||
        this.supplier_description ||
        this.panel_metadata ||
        this.ispanel
      ) {
        count += 1;
      }

      if (
        this.hasFilterItems(this.selected_category_list) ||
        this.hasFilterItems(this.selected_exclude_category_list) ||
        this.hasFilterItems(this.category_chips) ||
        this.hasFilterItems(this.tendle_cat_id_chips) ||
        this.hasFilterItems(this.selected_parent_categories)
      ) {
        count += 1;
      }

      if (
        this.hasFilterItems(this.agency_name_chips) ||
        this.hasFilterItems(this.agency_name_chips_exclude)
      ) {
        count += 1;
      }

      if (
        this.hasFilterItems(this.supplier_name_chips) ||
        this.hasFilterItems(this.supplier_name_chips_exclude) ||
        this.hasFilterItems(this.supplier_industry_chips) ||
        this.hasFilterItems(this.supplier_group_name_chips) ||
        this.hasFilterItems(this.supplier_id_chips) ||
        this.hasFilterItems(this.tendle_supp_id_chips) ||
        this.hasFilterItems(this.tendle_pub_id_chips)
      ) {
        count += 1;
      }

      if (this.hasFilterItems(this.selected_contact)) {
        count += 1;
      }

      if (
        this.hasFilterItems(this.selected_pub_clusters) ||
        this.hasFilterItems(this.selected_supp_clusters)
      ) {
        count += 1;
      }

      if (this.hasFilterItems(this.selected_procurement_method)) {
        count += 1;
      }

      if (
        this.hasFilterItems(this.selected_publisher_industry) ||
        this.hasFilterItems(this.selected_industry_ids)
      ) {
        count += 1;
      }

      if (
        this.hasFilterItems(this.selected_statuses) ||
        this.hasFilterItems(this.selected_types)
      ) {
        count += 1;
      }

      if (this.hasFilterItems(this.selected_territories)) {
        count += 1;
      }

      if (
        this.contract_start_enabled ||
        this.contract_end_enabled ||
        this.published_date_enabled ||
        this.hasFilterItems(this.closing_range) ||
        this.hasFilterItems(this.published_range) ||
        this.hasFilterItems(this.contract_start_range) ||
        this.hasFilterItems(this.contract_end_range) ||
        this.hasFilterItems(this.selected_months)
      ) {
        count += 1;
      }

      if ((this.amount_min != null && this.amount_min > 0) || (this.amount_max != null && this.amount_max > 0)) {
        count += 1;
      }

      if (this.hasFilterItems(this.request_chips) || this.hasFilterItems(this.panel_chips)) {
        count += 1;
      }

      return count;
    },

    emitFiltersChanged() {
      if (this._filtersChangedTimer != null) {
        clearTimeout(this._filtersChangedTimer);
      }
      this._filtersChangedTimer = setTimeout(() => {
        this._filtersChangedTimer = null;
        this.$emit('filters-changed', {
          keywordChips: [...this.keyword_chips],
          hasAdvancedFilters: this.hasAdvancedFilters,
          advancedFilterCount: this.advancedFilterCount,
        });
      }, 50);
    },

    addKeywordChip(value) {
      if (!value) return;
      const key = typeof value === 'object' && value !== null && 'key' in value ? value.key : String(value);
      if (!key || !key.trim()) return;
      const trimmed = key.trim();
      const exists = this.keyword_chips.some((c) => (c && c.key) === trimmed);
      if (!exists) {
        this.keyword_chips.push({ key: trimmed });
        this.keyword_chips = [...this.keyword_chips];
        this.supp_smart_tags = '';
      }
    },

    showKeywordsPanel() {
      const panelWasClosed = !this.filterOpenPanels.includes(0);
      if (panelWasClosed) {
        this.filterOpenPanels = [0, ...this.filterOpenPanels];
      }

      this.$nextTick(() => {
        if (panelWasClosed) {
          window.setTimeout(() => this.focusKeywordsCombobox(), 300);
        } else {
          this.focusKeywordsCombobox();
        }
      });
    },

    focusKeywordsCombobox() {
      const combobox = this.$refs.keywords;
      if (!combobox) return;

      if (typeof combobox.focus === 'function') {
        combobox.focus();
        return;
      }

      const input = combobox.$el && combobox.$el.querySelector('input');
      if (input) {
        input.focus();
      }
    },

    onKeywordsPaste(e) {
      const text = (e.clipboardData && e.clipboardData.getData('text')) || '';
      const keywords = splitKeywordTokens(text);
      if (keywords.length === 0) return;
      e.preventDefault();
      keywords.forEach((kw) => this.addKeywordChip({ key: kw }));
    },

    addBuyerChip(value) {
      if (value && !this.agency_name_chips.includes(value)) {
        this.agency_name_chips.push(value);
        this.pub_search = '';
      }
    },

    addExcludeBuyerChip(value) {
      if (value && !this.agency_name_chips_exclude.includes(value)) {
        this.agency_name_chips_exclude.push(value);
        this.exclude_pub_search = '';
      }
    },

    addSupplierChip(value) {
      if (value && !this.supplier_name_chips.includes(value)) {
        this.supplier_name_chips.push(value);
        this.supp_search = '';
      }
    },

    addSupplierExcludeChip(value) {
      if (value && !this.supplier_name_chips_exclude.includes(value)) {
        this.supplier_name_chips_exclude.push(value);
        this.exclude_supp_search = '';
      }
    },

    addSupplierGroupChip(value) {
      if (value && !this.supplier_group_name_chips.includes(value)) {
        this.supplier_group_name_chips.push(value);
        this.supplier_group_search = '';
      }
    },

    formatIndustriesForCombobox(industries, level = 1) {
      // Flatten nested industries and keep level info for indentation
      const flatten = (items, currentLevel = 1) => {
        let result = [];
        items.forEach(item => {
          const indent = currentLevel > 1 ? "  ".repeat(currentLevel - 1) : "";
          result.push({
            text: `${indent}${item.name}`,
            value: item.id,
            level: currentLevel,
            parent_id: item.parent_id || null
          });
          if (item.children && item.children.length > 0) {
            result = result.concat(flatten(item.children, currentLevel + 1));
          }
        });
        return result;
      };
      return flatten(industries);
    },

    buildSupplierIndustryChips(values) {
      if (!values) return [];
      const normalized = this.getArray(values);
      if (!Array.isArray(normalized)) return [];

      return normalized.map(val => {
        const normalizedVal = typeof val === "string" ? val.trim() : val;
        const match = this.industry_options_formatted.find(
          opt =>
            (typeof opt.text === "string" && opt.text.trim() === normalizedVal) ||
            opt.value === normalizedVal ||
            String(opt.value) === String(normalizedVal)
        );

        if (match) {
          return {
            text: typeof match.text === "string" ? match.text.trim() : match.text,
            value: match.value,
            level: match.level,
            parent_id: match.parent_id
          };
        }

        return { text: val, value: val };
      });
    },

    showDisclaimer() {

      if (this.disclaimer_msg_count == 0) {
        this.major_category_disclaimer = true
      }
      else {
        this.major_category_disclaimer = false
      }

      this.disclaimer_msg_count++;

    },

    updatePubCluster(list) {
      this.selected_pub_clusters = list;
      //////////////////////////////console.log("pub cluster: ", this.selected_pub_clusters);
    },

    updateSuppCluster(list) {
      this.selected_supp_clusters = list;
      //////////////////////////////console.log("pub cluster: ", this.selected_pub_clusters);
    },

    async update_subcategories() {
      let targets = [];
      for (var i in this.selected_parent_categories) {
        targets[i] = this.selected_parent_categories[i].code;
      }
      this.cat_items = await getSubCategories(targets).data;

      //////////////////////////////console.log("SUB CATS: ", this.cat_items);
    },

    filterDateDayOfMonth(isoDateStr) {
      if (!isoDateStr || typeof isoDateStr !== 'string') return 1;
      const parts = isoDateStr.split('-');
      if (parts.length < 3) return 1;
      const d = parseInt(parts[2], 10);
      return Number.isFinite(d) && d >= 1 && d <= 31 ? d : 1;
    },

    syncTimeframe(section, state) {
      const previousState = this.getTimeframeState(section);
      const nextState = !previousState.enabled && state.enabled
        ? this.createCurrentFinancialYearTimeframeState()
        : state;

      if (section === 'contract_start') {
        this.contractStartTimeframe = nextState;
      } else if (section === 'contract_end') {
        this.contractEndTimeframe = nextState;
      } else if (section === 'published') {
        this.publishedTimeframe = nextState;
      }
      this.applyTimeframeState(section, nextState);
    },

    getTimeframeState(section) {
      if (section === 'contract_start') {
        return this.contractStartTimeframe;
      } else if (section === 'contract_end') {
        return this.contractEndTimeframe;
      } else if (section === 'published') {
        return this.publishedTimeframe;
      }
      return createDefaultTimeframeState();
    },

    createCurrentFinancialYearTimeframeState() {
      return {
        ...createDefaultTimeframeState(),
        enabled: true
      };
    },

    applyTimeframeState(section, state) {
      const computed = computeTimeframeDates(state);
      if (!computed) return;

      if (section === 'contract_start') {
        this.contract_start_enabled = state.enabled;
        if (!state.enabled) return;
        this.year_min = computed.fromYear;
        this.year_max = computed.toYear;
        this.set_contract_month_start(this.months[computed.fromMonth], 1);
        this.set_contract_month_end(this.months[computed.toMonth]);
        this.contract_month_start.date = computed.fromDate;
        this.contract_month_end.date = computed.toDate;
      } else if (section === 'contract_end') {
        this.contract_end_enabled = state.enabled;
        if (!state.enabled) return;
        this.end_year_min = computed.fromYear;
        this.end_year_max = computed.toYear;
        this.set_expiry_month_start(this.months[computed.fromMonth], 1);
        this.set_expiry_month_end(this.months[computed.toMonth]);
        this.expiry_month_start.date = computed.fromDate;
        this.expiry_month_end.date = computed.toDate;
      } else if (section === 'published') {
        this.published_date_enabled = state.enabled;
        if (!state.enabled) return;
        this.pub_year_min = computed.fromYear;
        this.pub_year_max = computed.toYear;
        this.set_published_month_start(this.months[computed.fromMonth], 1);
        this.set_published_month_end(this.months[computed.toMonth]);
        this.published_month_start.date = computed.fromDate;
        this.published_month_end.date = computed.toDate;
      }
    },

    loadTimeframeFromQuery(section, fromIso, toIso) {
      const state = createTimeframeStateFromDates(fromIso, toIso);
      if (section === 'contract_start') {
        this.contractStartTimeframe = state;
      } else if (section === 'contract_end') {
        this.contractEndTimeframe = state;
      } else if (section === 'published') {
        this.publishedTimeframe = state;
      }
      this.applyTimeframeState(section, state);
    },

    syncAllTimeframes() {
      this.applyTimeframeState('contract_start', this.contractStartTimeframe);
      this.applyTimeframeState('contract_end', this.contractEndTimeframe);
      this.applyTimeframeState('published', this.publishedTimeframe);
    },

    set_contract_month_start(month, dayOfMonth) {

      var index = this.months.indexOf(month);

      var lastDayInMonth = new Date(this.year_min, index + 1, 0).getDate();
      var rawDay = dayOfMonth != null ? dayOfMonth : 1;
      var day = Math.min(Math.max(1, rawDay), lastDayInMonth);

      var dte = new Date(this.year_min, index, day);

      var converted_date = dte.getFullYear() + "-" + (dte.getMonth() + 1).toString().padStart(2, '0') + "-" + dte.getDate().toString().padStart(2, '0');

      this.contract_month_start.name = month;
      this.contract_month_start.date = converted_date; //this.year_min

      //////////////////////////////console.log("start month: ", this.contract_month_start, " YEARS[0]: ", this.year_min);

    },

    set_published_month_start(month, dayOfMonth) {

      var index = this.months.indexOf(month);

      var lastDayInMonth = new Date(this.pub_year_min, index + 1, 0).getDate();
      var rawDay = dayOfMonth != null ? dayOfMonth : 1;
      var day = Math.min(Math.max(1, rawDay), lastDayInMonth);

      var dte = new Date(this.pub_year_min, index, day);

      var converted_date = dte.getFullYear() + "-" + (dte.getMonth() + 1).toString().padStart(2, '0') + "-" + dte.getDate().toString().padStart(2, '0');

      this.published_month_start.name = month;
      this.published_month_start.date = converted_date; //this.year_min

      //////////////////////////////console.log("start month: ", this.contract_month_start, " YEARS[0]: ", this.year_min);

    },

    set_published_month_end(month) {

      var index = this.months.indexOf(month);

      var lastday = function (y, m) {
        return new Date(y, m + 1, 0).getDate();
      }

      var dte = new Date(this.pub_year_max, index, lastday(this.pub_year_max, index))

      var converted_date = dte.getFullYear() + "-" + (dte.getMonth() + 1).toString().padStart(2, '0') + "-" + dte.getDate().toString().padStart(2, '0');

      this.published_month_end.name = month; //this.year_max
      this.published_month_end.date = converted_date;

      //////////////////////////////console.log("end month: ", this.contract_month_end, " YEARS[1]: ", this.year_max);

    },

    set_contract_month_end(month) {

      var index = this.months.indexOf(month);

      var lastday = function (y, m) {
        return new Date(y, m + 1, 0).getDate();
      }

      var dte = new Date(this.year_max, index, lastday(this.year_max, index))

      var converted_date = dte.getFullYear() + "-" + (dte.getMonth() + 1).toString().padStart(2, '0') + "-" + dte.getDate().toString().padStart(2, '0');

      this.contract_month_end.name = month; //this.year_max
      this.contract_month_end.date = converted_date;

      //////////////////////////////console.log("end month: ", this.contract_month_end, " YEARS[1]: ", this.year_max);

    },

    set_expiry_month_start(month, dayOfMonth) {

      var index = this.months.indexOf(month);

      var lastDayInMonth = new Date(this.end_year_min, index + 1, 0).getDate();
      var rawDay = dayOfMonth != null ? dayOfMonth : 1;
      var day = Math.min(Math.max(1, rawDay), lastDayInMonth);

      var dte = new Date(this.end_year_min, index, day);

      var converted_date = dte.getFullYear() + "-" + (dte.getMonth() + 1).toString().padStart(2, '0') + "-" + dte.getDate().toString().padStart(2, '0');

      this.expiry_month_start.name = month;
      this.expiry_month_start.date = converted_date; //this.year_min

      //////////////////////////////console.log("start month: ", this.expiry_month_start);

    },

    set_expiry_month_end(month) {

      var index = this.months.indexOf(month);

      var lastday = function (y, m) {
        return new Date(y, m + 1, 0).getDate();
      }

      var dte = new Date(this.end_year_max, index, lastday(this.end_year_max, index))

      var converted_date = dte.getFullYear() + "-" + (dte.getMonth() + 1).toString().padStart(2, '0') + "-" + dte.getDate().toString().padStart(2, '0');

      this.expiry_month_end.name = month; //this.year_max
      this.expiry_month_end.date = converted_date;

      //////////////////////////////console.log("end month: ", this.expiry_month_end);

    },

    remove_blank_chip(chips) {
      var filtered = chips.filter(function (value, index, arr) {
        return value != null;
      });
      return filtered;
    },

    hideFilter() {
      this.$emit('close');
    },

    year() {
      var d = new Date();
      return d.getFullYear();
    },

    clear_contract_start() {
      this.contract_start_range = [];
    },
    clear_years() {
      this.years = [2005, 2030];
      this.contract_start_enabled = false;
      this.end_years = [2005, 2030];
      this.pub_years = [2005, 2030];
      this.contract_end_enabled = false;
      this.published_date_enabled = false;
      this.contractStartTimeframe = createDefaultTimeframeState();
      this.contractEndTimeframe = createDefaultTimeframeState();
      this.publishedTimeframe = createDefaultTimeframeState();
    },
    clear_contract_end() {
      this.contract_end_range = [];
    },
    clear_selected_months() {
      this.selected_months = [];
    },
    clear_published_date() {
      this.published_range = [];
    },
    clear_closing_date() {
      this.closing_range = [];
    },
    remove_kw(item) {
      this.keyword_chips.splice(this.keyword_chips.indexOf(item), 1);
      this.keyword_chips = [...this.keyword_chips];
    },
    remove_category(item) {
      this.category_chips.splice(this.category_chips.indexOf(item), 1);
      this.category_chips = [...this.category_chips];
    },

    remove_major_category(item) {
      this.selected_parent_categories.splice(this.selected_parent_categories.indexOf(item), 1);
      this.selected_parent_categories = [...this.selected_parent_categories];
    },

    remove_supp_name(item) {
      this.supplier_name_chips.splice(
        this.supplier_name_chips.indexOf(item),
        1
      );
      this.supplier_name_chips = [...this.supplier_name_chips];
    },
    remove_supp_industry(item) {
      this.supplier_industry_chips.splice(
        this.supplier_industry_chips.indexOf(item),
        1
      );
      this.supplier_industry_chips = [...this.supplier_industry_chips];
    },
    remove_supplier_group_name(item) {
      this.supplier_group_name_chips.splice(
        this.supplier_group_name_chips.indexOf(item),
        1
      );
      this.supplier_group_name_chips = [...this.supplier_group_name_chips];
    },
    remove_excluded_supp_name(item) {
      this.supplier_name_chips_exclude.splice(
        this.supplier_name_chips_exclude.indexOf(item),
        1
      );
      this.supplier_name_chips_exclude = [...this.supplier_name_chips_exclude];
    },
    remove_supp_id(item) {
      this.supplier_id_chips.splice(this.supplier_id_chips.indexOf(item), 1);
      this.supplier_id_chips = [...this.supplier_id_chips];
    },
    remove_tendle_cat_id(item) {
      this.tendle_cat_id_chips.splice(
        this.tendle_cat_id_chips.indexOf(item),
        1
      );
      this.tendle_cat_id_chips = [...this.tendle_cat_id_chips];
    },
    remove_tendle_supp_id(item) {
      this.tendle_supp_id_chips.splice(
        this.tendle_supp_id_chips.indexOf(item),
        1
      );
      this.tendle_supp_id_chips = [...this.tendle_supp_id_chips];
    },
    remove_tendle_pub_id(item) {
      this.tendle_pub_id_chips.splice(
        this.tendle_pub_id_chips.indexOf(item),
        1
      );
      this.tendle_pub_id_chips = [...this.tendle_pub_id_chips];
    },
    remove_agency_name(item) {
      this.agency_name_chips.splice(this.agency_name_chips.indexOf(item), 1);
      this.agency_name_chips = [...this.agency_name_chips];
    },
    remove_buyer_industry(item) {
      if (item && item.id != null) {
        this.selected_industry_ids = this.selected_industry_ids.filter(id => id !== item.id);
      } else {
        const index = this.selected_publisher_industry.indexOf(item);
        if (index > -1) {
          this.selected_publisher_industry.splice(index, 1);
          this.selected_publisher_industry = [...this.selected_publisher_industry];
        }
      }
    },
    rehydratePublisherIndustry(names) {
      const ids = Object.values(this.cofog_flat_map)
        .filter(item => names.includes(item.name))
        .map(item => item.id);
      if (ids.length) {
        this.selected_industry_ids = ids;
      } else {
        this.selected_publisher_industry = names.map(n => ({ name: n }));
      }
    },
    remove_procurement_method(item) {
      this.selected_procurement_method.splice(this.selected_procurement_method.indexOf(item), 1);
      this.selected_procurement_method = [...this.selected_procurement_method];
    },
    remove_contact(item) {
      this.selected_contact.splice(this.selected_contact.indexOf(item), 1);
      this.selected_contact = [...this.selected_contact];
    },
    remove_category_list(item) {
      this.selected_category_list.splice(this.selected_category_list.indexOf(item), 1);
      this.selected_category_list = [...this.selected_category_list];
    },
    remove_exclude_category_list(item) {
      this.selected_exclude_category_list.splice(this.selected_exclude_category_list.indexOf(item), 1);
      this.selected_exclude_category_list = [...this.selected_exclude_category_list];
    },
    remove_excluded_agency_name(item) {
      this.agency_name_chips_exclude.splice(
        this.agency_name_chips_exclude.indexOf(item),
        1
      );
      this.agency_name_chips_exclude = [...this.agency_name_chips_exclude];
    },

    remove_request(item) {
      this.request_chips.splice(this.request_chips.indexOf(item), 1);
      this.request_chips = [...this.request_chips];
    },
    remove_panel(item) {
      this.panel_chips.splice(this.panel_chips.indexOf(item), 1);
      this.panel_chips = [...this.panel_chips];
    },
    is_panel_off() {
      this.ispanel = false;
    },
    exclude_panel_off() {
      this.exclude_panel = false;
    },
    and_query_off() {
      this.and_query = false;
    },
    exclude_grant_off() {
      this.exclude_grant = false;
    },
    is_consultancy_off() {
      this.isconsultancy = false;
    },
    is_parent_off() {
      this.isparent = false;
    },
    setAmountRange() {
      this.amount_min = null;
      this.amount_max = null;
    },

    customAmount(flag) {
      this.custom_amount = flag;
    },

    formatDateToCustomString(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    },

    submitSearch() {
      this.syncAllTimeframes();
      this.search_params["category"] = this.remove_blank_chip(this.selected_category_list);

      if (this.selected_exclude_category_list && this.selected_exclude_category_list.length > 0) {
        this.search_params["exclude_category"] = this.remove_blank_chip(this.selected_exclude_category_list);
      } else {
        delete this.search_params["exclude_category"];
      }

      var kw_chips = [];
      if (this.keyword_chips.length > 0) {

        for (var i = 0; i < this.keyword_chips.length; i++) {
          kw_chips[i] = this.keyword_chips[i].key;
        }
      }

      this.search_params["tender"] = this.remove_blank_chip(kw_chips);

      if (this.selected_contact.length > 0) {
        var contacts = [];
        //////console.log("this.selected_contact: "+ JSON.stringify(this.selected_contact));
        for (var i = 0; i < this.selected_contact.length; i++) {
          contacts[i] = this.selected_contact[i].name;
        }
        this.search_params["contact"] = contacts;
      }
      else {
        //////console.log("DELETING CONTACT FIELD: ");
        delete this.search_params["contact"]
      }

      if (this.supplier_smart_tags) {
        this.search_params["supplier_smart_tags"] = this.supplier_smart_tags;
      }
      else {
        delete this.search_params["supplier_smart_tags"]
      }

      if (this.supplier_description) {
        this.search_params["supplier_description"] = this.supplier_description;
      }
      else {
        delete this.search_params["supplier_description"]
      }

      if (this.panel_metadata) {
        this.search_params["panel_metadata"] = this.panel_metadata;
      }
      else {
        delete this.search_params["panel_metadata"]
      }

      if (this.selected_parent_categories) {
        var parent_cats = [];
        for (var i = 0; i < this.selected_parent_categories.length; i++) {

          parent_cats[i] = this.selected_parent_categories[i].id;
        }
        this.search_params["major_category_id"] = parent_cats;
      }

      if (this.selected_pub_clusters) {
        var pub_clusters = [];
        for (var i = 0; i < this.selected_pub_clusters.length; i++) {
          pub_clusters[i] = Number(this.selected_pub_clusters[i].id);
        }
        this.search_params["buyer_cluster"] = pub_clusters;
      }

      if (this.selected_supp_clusters) {
        var supp_clusters = [];
        for (var i = 0; i < this.selected_supp_clusters.length; i++) {
          supp_clusters[i] = Number(this.selected_supp_clusters[i].id);
        }
        this.search_params["supplier_cluster"] = supp_clusters;
      }

      /* if (this.category_chips) {
          this.search_params["category"] = this.category_chips;
        } */

      if (this.amount_min > 0) {
        this.search_params["amount_from"] = this.amount_min;
      }
      else {
        delete this.search_params["amount_from"];
      }

      if (this.amount_max > 0) {
        this.search_params["amount_to"] = this.amount_max;
      }
      else {
        delete this.search_params["amount_to"];
      }

      this.search_params["status"] = this.selected_statuses;
      this.search_params["disposition"] = this.selected_types;

      if (this.contract_start_enabled) {
        if (this.contract_month_start && this.contract_month_start.date) {
          this.set_contract_month_start(
            this.contract_month_start.name,
            this.filterDateDayOfMonth(this.contract_month_start.date)
          );
          this.search_params["contract_start_from"] = this.contract_month_start.date;
        }

        if (this.contract_month_end && this.contract_month_end.date) {
          this.set_contract_month_end(this.contract_month_end.name);
          this.search_params["contract_start_to"] = this.contract_month_end.date;
        }
      } else {
        delete this.search_params["contract_start_from"];
        delete this.search_params["contract_start_to"];
        delete this.search_params["contract_year_from"];
        delete this.search_params["contract_year_to"];
      }

      if (this.published_date_enabled) {
        if (this.published_month_start && this.published_month_start.date) {
          this.set_published_month_start(
            this.published_month_start.name,
            this.filterDateDayOfMonth(this.published_month_start.date)
          );
          this.search_params["published_from"] = this.published_month_start.date;
        }

        if (this.published_month_end && this.published_month_end.date) {
          this.set_published_month_end(this.published_month_end.name);
          this.search_params["published_to"] = this.published_month_end.date;
        }
      } else {
        delete this.search_params["published_from"];
        delete this.search_params["published_to"];
        delete this.search_params["published_year_from"];
        delete this.search_params["published_year_to"];
      }

      if (this.contract_end_enabled) {
        if (this.expiry_month_start && this.expiry_month_start.date) {
          this.set_expiry_month_start(
            this.expiry_month_start.name,
            this.filterDateDayOfMonth(this.expiry_month_start.date)
          );
          this.search_params["expiry_date_from"] = this.expiry_month_start.date;
        }

        if (this.expiry_month_end && this.expiry_month_end.date) {
          this.set_expiry_month_end(this.expiry_month_end.name);
          this.search_params["expiry_date_to"] = this.expiry_month_end.date;
        }
      } else {
        delete this.search_params["expiry_date_from"];
        delete this.search_params["expiry_date_to"];
      }

      this.search_params["publisher_industry"] = this.selected_publisher_industry.map(i => i.name);
      this.remove_blank_chip(this.selected_publisher_industry);

      this.search_params["procurement_method"] = this.remove_blank_chip(this.selected_procurement_method);


      var pub_chips = [];
      if (this.agency_name_chips) {
        for (var i = 0; i < this.agency_name_chips.length; i++) {
          pub_chips[i] = this.agency_name_chips[i].key;
        }
      }

      this.search_params["publisher"] = this.remove_blank_chip(pub_chips);

      var exclude_pub_chips = [];
      if (this.agency_name_chips_exclude) {
        for (var i = 0; i < this.agency_name_chips_exclude.length; i++) {
          exclude_pub_chips[i] = this.agency_name_chips_exclude[i].key;
        }
      }

      this.search_params["exclude_publisher"] = this.remove_blank_chip(
        exclude_pub_chips
      );

      var supp_ind_chips = [];
      if (this.supplier_industry_chips) {
        for (var i = 0; i < this.supplier_industry_chips.length; i++) {
          const chip = this.supplier_industry_chips[i];
          supp_ind_chips[i] = (chip && (chip.text || chip.key || chip.value)) ? (chip.text || chip.key || chip.value) : chip;
          if (typeof supp_ind_chips[i] === "string") {
            supp_ind_chips[i] = supp_ind_chips[i].trim();
          }
        }
      }

      this.search_params["supplier_industry"] = this.remove_blank_chip(supp_ind_chips);

      var supp_chips = [];
      if (this.supplier_name_chips) {
        for (var i = 0; i < this.supplier_name_chips.length; i++) {
          supp_chips[i] = this.supplier_name_chips[i].key;
        }
      }

      this.search_params["supplier"] = this.remove_blank_chip(supp_chips);
      var supplier_group_chips = [];
      if (this.supplier_group_name_chips) {
        for (var i = 0; i < this.supplier_group_name_chips.length; i++) {
          supplier_group_chips[i] = this.supplier_group_name_chips[i].key;
        }
      }
      this.search_params["supplier_group_name"] = this.remove_blank_chip(supplier_group_chips);
      this.search_params["supplier_company_number"] = this.supplier_id_chips;
      this.search_params["supplier_id"] = this.tendle_supp_id_chips;
      this.search_params["category_id"] = this.tendle_cat_id_chips;
      this.search_params["publisher_id"] = this.tendle_pub_id_chips;
      //remove white space from abn

      var exclude_supp_chips = [];
      if (this.supplier_name_chips_exclude) {
        for (var i = 0; i < this.supplier_name_chips_exclude.length; i++) {
          exclude_supp_chips[i] = this.supplier_name_chips_exclude[i].key;
        }
      }

      this.search_params["exclude_supplier"] = this.remove_blank_chip(
        exclude_supp_chips
      );

      for (
        var i = 0;
        i < this.search_params["supplier_company_number"].length;
        i++
      ) {
        var s = this.search_params["supplier_company_number"][i];
        this.search_params["supplier_company_number"][
          i
        ] = this.remove_white_space(s);
      }
      this.search_params["supplier_company_number"] = this.supplier_id_chips;
      this.search_params["government"] = this.selected_territories;
      this.search_params["recid"] = this.request_chips;
      this.search_params["son_id"] = this.panel_chips;

      if (!this.ispanel) {
        delete this.search_params.ispanel;
      }
      else {
        this.search_params["ispanel"] = this.ispanel;
      }

      if (!this.isindigenous) {
        delete this.search_params.isindigenous;
      }
      else {
        this.search_params["isindigenous"] = this.isindigenous;
      }

      if (!this.exclude_panel) {
        delete this.search_params.exclude_panel;
      }
      else {
        this.search_params["exclude_panel"] = this.exclude_panel;
      }

      if (!this.and_query) {
        delete this.search_params.and_query;
      }
      else {
        this.search_params["and_query"] = this.and_query;
      }

      if (!this.exclude_grant) {
        delete this.search_params.exclude_grant;
      }
      else {
        this.search_params["exclude_grant"] = this.exclude_grant;
      }

      if (!this.isconsultancy) {
        delete this.search_params.isconsultancy;
      }
      else {
        this.search_params["isconsultancy"] = this.isconsultancy;
      }

      if (!this.isparent) {
        delete this.search_params.isparent;
      }
      else {
        this.search_params["isparent"] = this.isparent;
      }


      //bus.$emit("submit-search", this.search_params);
      //////////////////////////////console.log(this.$route);
      if (this.$route.query.viewIndex) {
        this.search_params["viewIndex"] = this.$route.query.viewIndex;
      }

      this.search_params["newSearch"] = true;

      var qry = JSON.parse(JSON.stringify(this.$route.query));

      if (qry.size) {
        this.search_params["size"] = qry.size;
      }

      this.$emit('search', { ...this.search_params });
    },

    async populateFilters(query) {

      this.reset_filters();

      if (!query.hasOwnProperty("tender")) {
        //////////////////////////////console.log('!query.hasOwnProperty("tender")');
        //check if this is a filter on supplier or publisher only
        if (query.hasOwnProperty("supplier_company_number")) {
          this.supplier_id_chips = this.getArray(
            query["supplier_company_number"]
          );
        } if (query.hasOwnProperty("supplier")) {
          query["supplier"] = this.getArray(query["supplier"]);
          for (var i = 0; i < query["supplier"].length; i++) {
            this.supplier_name_chips[i] = { key: query["supplier"][i] }; // convert to dict as that is what is created in reverse
          }
        }
        if (query.hasOwnProperty("supplier_industry")) {
          query["supplier_industry"] = this.getArray(query["supplier_industry"]);

          this.pending_supplier_industries = query["supplier_industry"];
          this.supplier_industry_chips = this.buildSupplierIndustryChips(query["supplier_industry"]);
        }
        if (query.hasOwnProperty("supplier_group_name")) {
          query["supplier_group_name"] = this.getArray(query["supplier_group_name"]);
          for (var i = 0; i < query["supplier_group_name"].length; i++) {
            this.supplier_group_name_chips[i] = { key: query["supplier_group_name"][i] };
          }
        } else if (query.hasOwnProperty("supplier_group")) {
          // Backward compatibility: support old "supplier_group" parameter
          query["supplier_group"] = this.getArray(query["supplier_group"]);
          for (var i = 0; i < query["supplier_group"].length; i++) {
            this.supplier_group_name_chips[i] = { key: query["supplier_group"][i] };
          }
        }
        if (query.hasOwnProperty("contact")) {
          query["contact"] = this.getArray(query["contact"]);
          for (var i = 0; i < query["contact"].length; i++) {
            this.selected_contact[i] = { key: query["contact"][i] }; // convert to dict as that is what is created in reverse
          }
        }
        if (query.hasOwnProperty("publisher")) {
          query["publisher"] = this.getArray(query["publisher"]);
          for (var i = 0; i < query["publisher"].length; i++) {
            this.agency_name_chips[i] = { key: query["publisher"][i] }; // convert to dict as that is what is created in reverse
          }
        } if (query.hasOwnProperty("exclude_publisher")) {
          query["exclude_publisher"] = this.getArray(
            query["exclude_publisher"]
          );
          for (var i = 0; i < query["exclude_publisher"].length; i++) {
            this.agency_name_chips_exclude[i] = {
              key: query["exclude_publisher"][i]
            }; // convert to dict as that is what is created in reverse
          }
        } if (query.hasOwnProperty("exclude_supplier")) {
          query["exclude_supplier"] = this.getArray(query["exclude_supplier"]);
          for (var i = 0; i < query["exclude_supplier"].length; i++) {
            this.supplier_name_chips_exclude[i] = {
              key: query["exclude_supplier"][i]
            }; // convert to dict as that is what is created in reverse
          }
        }
        if (query.hasOwnProperty("category_id")) {
          query["category_id"] = this.getArray(query["category_id"]);
          this.tendle_cat_id_chips = this.getArray(query["category_id"]);
        }
        if (query.hasOwnProperty("major_category_id")) {
          query["major_category_id"] = this.getArray(query["major_category_id"]);
          //////////////////////////////console.log("PARENT HEIRARCHY: ", query["major_category_id"]);
          for (var i = 0; i < query["major_category_id"].length; i++) {
            //this.selected_parent_categories[i] = { key: query["parent_category"][i] };
            this.selected_parent_categories[i] = this.parent_category_options.find(x => x.id === parseInt(query["major_category_id"][i])); // convert to dict as that is what is created in reverse
          }
        }
        if (query.hasOwnProperty("supplier_id")) {
          query["supplier_id"] = this.getArray(query["supplier_id"]);
          this.tendle_supp_id_chips = this.getArray(query["supplier_id"]);
        }
        if (query.hasOwnProperty("publisher_id")) {
          query["publisher_id"] = this.getArray(query["publisher_id"]);
          this.tendle_pub_id_chips = this.getArray(query["publisher_id"]);
        }

        if (query.hasOwnProperty("amount_from")) {
          this.amount_min = query["amount_from"];
        }

        if (query.hasOwnProperty("amount_to")) {
          this.amount_max = query["amount_to"];
        }

        if (query["contract_start_from"] || query["contract_start_to"]) {
          this.loadTimeframeFromQuery(
            'contract_start',
            query["contract_start_from"],
            query["contract_start_to"]
          );
        }

        if (query["published_from"] || query["published_to"]) {
          this.loadTimeframeFromQuery(
            'published',
            query["published_from"],
            query["published_to"]
          );
        }

        if (query["expiry_date_from"] || query["expiry_date_to"]) {
          this.loadTimeframeFromQuery(
            'contract_end',
            query["expiry_date_from"],
            query["expiry_date_to"]
          );
        }

        /* if (query.hasOwnProperty("category")) {
          query["category"] = this.getArray(query["category"]);
          for (var i = 0; i < query["category"].length; i++) {
            this.category_chips[i] = query["category"][i]; ; // convert to dict as that is what is created in reverse
          }
        } */

        if (query.hasOwnProperty("category")) {
          query["category"] = this.getArray(query["category"]);
          for (var i = 0; i < query["category"].length; i++) {
            this.selected_category_list[i] = query["category"][i]; // convert to dict as that is what is created in reverse
          }
        }

        if (query.hasOwnProperty("exclude_category")) {
          this.selected_exclude_category_list = this.getArray(query["exclude_category"]);
        }

        if (query.hasOwnProperty("publisher_industry")) {
          this.rehydratePublisherIndustry(this.getArray(query["publisher_industry"]));
        }

        if (query.hasOwnProperty("procurement_method")) {
          this.selected_procurement_method = this.getArray(query["procurement_method"]);
        }


        if (query.hasOwnProperty("supplier_cluster")) {
          this.injectSuppClusters = this.getArray(query["supplier_cluster"]);
        }

        if (query.hasOwnProperty("buyer_cluster")) {
          this.injectPubClusters = this.getArray(query["buyer_cluster"]);
        }

        if (this.getArray(query["disposition"]) && this.getArray(query["disposition"]).length > 0)
          this.selected_types = this.getArray(query["disposition"]);


        if (query.hasOwnProperty("exclude_government")) {
          var excluded = this.getArray(query["exclude_government"]);
          if (this.territory_options && this.territory_options.length) {
            this.selected_territories = this.territory_options.map(t => t.value).filter(v => !excluded.includes(v));
          } else {
            this.injectExcludeGovernment = excluded;
          }
        } else if (this.getArray(query["government"]) && this.getArray(query["government"]).length > 0) {
          this.selected_territories = this.getArray(query["government"]);
        }

        if (query.hasOwnProperty("ispanel")) {
          this.ispanel = query["ispanel"]
        }
        if (query.hasOwnProperty("exclude_panel")) {
          this.exclude_panel = query["exclude_panel"]
        }
        if (query.hasOwnProperty("and_query")) {
          this.and_query = query["and_query"]
        }
        if (query.hasOwnProperty("exclude_grant")) {
          this.exclude_grant = query["exclude_grant"]
        }
        if (query.hasOwnProperty("isconsultancy")) {
          this.isconsultancy = query["isconsultancy"]
        }
        if (query.hasOwnProperty("isparent")) {
          this.isparent = query["isparent"]
        }
        if (query.hasOwnProperty("isindigenous")) {
          this.isindigenous = query["isindigenous"]
        }


        this.request_chips = this.getArray(query["recid"]);
        this.panel_chips = this.getArray(query["son_id"]);
        /* this.ispanel = query["ispanel"];
        this.exclude_panel = query["exclude_panel"];
        this.and_query = query["and_query"];
        this.exclude_grant = query["exclude_grant"];
        this.isconsultancy = query["isconsultancy"];
        this.isparent = query["isparent"]; */

      } else {
        //this.keyword_chips = this.getArray(query.tender);

        if (query.hasOwnProperty("tender")) {
          query["tender"] = this.getArray(query["tender"]);

          for (var i = 0; i < query["tender"].length; i++) {
            this.keyword_chips[i] = { key: query["tender"][i] }; // convert to dict as that is what is created in reverse
          }
        }

        if (query.hasOwnProperty("contact")) {
          query["contact"] = this.getArray(query["contact"]);
          ////console.log (JSON.stringify(query["contact"]));
          for (var i = 0; i < query["contact"].length; i++) {
            this.selected_contact[i] = { name: query["contact"][i] }; // convert to dict as that is what is created in reverse
          }
        }

        if (query.hasOwnProperty("supplier_smart_tags")) {
          this.supplier_smart_tags = query["supplier_smart_tags"]
        }

        if (query.hasOwnProperty("supplier_description")) {
          this.supplier_description = query["supplier_description"]
        }

        if (query.hasOwnProperty("panel_metadata")) {
          this.panel_metadata = query["panel_metadata"]
        }

        if (query.hasOwnProperty("supplier_cluster")) {

          this.injectSuppClusters = this.getArray(query["supplier_cluster"]);
          ////////////////////////////console.log(this.injectSuppClusters);
        }

        if (query.hasOwnProperty("buyer_cluster")) {
          this.injectPubClusters = this.getArray(query["buyer_cluster"]);
        }

        /* if (query.hasOwnProperty("category")) {
          query["category"] = this.getArray(query["category"]);
          for (var i = 0; i < query["category"].length; i++) {
            this.category_chips[i] = query["category"][i]; // convert to dict as that is what is created in reverse
          }
        } */

        if (query.hasOwnProperty("category")) {
          query["category"] = this.getArray(query["category"]);
          for (var i = 0; i < query["category"].length; i++) {
            this.selected_category_list[i] = query["category"][i]; // convert to dict as that is what is created in reverse
          }
        }

        if (query.hasOwnProperty("exclude_category")) {
          this.selected_exclude_category_list = this.getArray(query["exclude_category"]);
        }

        if (query.hasOwnProperty("major_category_id")) {

          query["major_category_id"] = this.getArray(query["major_category_id"]);
          //////////////////////////////console.log("PARENT HEIRARCHY: ", query["major_category_id"]);
          for (var i = 0; i < query["major_category_id"].length; i++) {
            //this.selected_parent_categories[i] = { key: query["parent_category"][i] };
            this.selected_parent_categories[i] = this.parent_category_options.find(x => x.id === parseInt(query["major_category_id"][i])); // convert to dict as that is what is created in reverse
          }
        }

        if (query.hasOwnProperty("amount_from")) {
          this.amount_min = query["amount_from"];
        }

        if (query.hasOwnProperty("amount_to")) {
          this.amount_max = query["amount_to"];
        }

        //////////////////////////////console.log("QUERY: " + JSON.stringify(query));

        if (query.hasOwnProperty("supplier")) {
          query["supplier"] = this.getArray(query["supplier"]);

          for (var i = 0; i < query["supplier"].length; i++) {
            this.supplier_name_chips[i] = { key: query["supplier"][i] }; // convert to dict as that is what is created in reverse
          }
        }

        if (query.hasOwnProperty("supplier_industry")) {
          query["supplier_industry"] = this.getArray(query["supplier_industry"]);

          this.pending_supplier_industries = query["supplier_industry"];
          this.supplier_industry_chips = this.buildSupplierIndustryChips(query["supplier_industry"]);
        }
        if (query.hasOwnProperty("supplier_group_name")) {
          query["supplier_group_name"] = this.getArray(query["supplier_group_name"]);
          for (var i = 0; i < query["supplier_group_name"].length; i++) {
            this.supplier_group_name_chips[i] = { key: query["supplier_group_name"][i] };
          }
        } else if (query.hasOwnProperty("supplier_group")) {
          // Backward compatibility: support old "supplier_group" parameter
          query["supplier_group"] = this.getArray(query["supplier_group"]);
          for (var i = 0; i < query["supplier_group"].length; i++) {
            this.supplier_group_name_chips[i] = { key: query["supplier_group"][i] };
          }
        }

        if (query.hasOwnProperty("exclude_supplier")) {
          query["exclude_supplier"] = this.getArray(query["exclude_supplier"]);
          for (var i = 0; i < query["exclude_supplier"].length; i++) {
            this.supplier_name_chips_exclude[i] = {
              key: query["exclude_supplier"][i]
            }; // convert to dict as that is what is created in reverse
          }
        }

        if (query.hasOwnProperty("publisher_industry")) {
          this.rehydratePublisherIndustry(this.getArray(query["publisher_industry"]));
        }

        if (query.hasOwnProperty("procurement_method")) {
          this.selected_procurement_method = this.getArray(query["procurement_method"]);
        }

        if (query.hasOwnProperty("category_list")) {
          this.selected_category_list = this.getArray(query["category_list"]);
        }

        //query["publisher"] = this.getArray(query["publisher"]);
        if (query.hasOwnProperty("publisher")) {
          query["publisher"] = this.getArray(query["publisher"]);
          for (var i = 0; i < query["publisher"].length; i++) {
            this.agency_name_chips[i] = { key: query["publisher"][i] }; // convert to dict as that is what is created in reverse
          }
        }

        if (query.hasOwnProperty("exclude_publisher")) {
          query["exclude_publisher"] = this.getArray(
            query["exclude_publisher"]
          );
          for (var i = 0; i < query["exclude_publisher"].length; i++) {
            this.agency_name_chips_exclude[i] = {
              key: query["exclude_publisher"][i]
            }; // convert to dict as that is what is created in reverse
          }
        }

        if (this.getArray(query["status"]) && this.getArray(query["status"]).length > 0)
          this.selected_statuses = this.getArray(query["status"]);

        if (this.getArray(query["disposition"]) && this.getArray(query["disposition"]).length > 0)
          this.selected_types = this.getArray(query["disposition"]);

        this.amount_min = query["amount_from"];
        this.amount_max = query["amount_to"];

        if (query["contract_year_from"] || query["contract_year_to"]) {
          this.contract_start_enabled = true;
          this.year_min = query["contract_year_from"];
          this.year_max = query["contract_year_to"];
        }

        if (query["contract_start_from"] || query["contract_start_to"]) {
          this.loadTimeframeFromQuery(
            'contract_start',
            query["contract_start_from"],
            query["contract_start_to"]
          );
        }

        if (query["published_from"] || query["published_to"]) {
          this.loadTimeframeFromQuery(
            'published',
            query["published_from"],
            query["published_to"]
          );
        }

        if (query["expiry_date_from"] || query["expiry_date_to"]) {
          this.loadTimeframeFromQuery(
            'contract_end',
            query["expiry_date_from"],
            query["expiry_date_to"]
          );
        }

        if (query.hasOwnProperty("supplier_company_number")) {
          query["supplier_company_number"] = this.getArray(
            query["supplier_company_number"]
          );
          this.supplier_id_chips = this.getArray(
            query["supplier_company_number"]
          );
        }

        if (query.hasOwnProperty("supplier_id")) {
          query["supplier_id"] = this.getArray(query["supplier_id"]);
          this.tendle_supp_id_chips = this.getArray(query["supplier_id"]);
        }

        if (query.hasOwnProperty("category_id")) {
          query["category_id"] = this.getArray(query["category_id"]);
          this.tendle_cat_id_chips = this.getArray(query["category_id"]);
        }

        if (query.hasOwnProperty("publisher_id")) {
          query["publisher_id"] = this.getArray(query["publisher_id"]);
          this.tendle_pub_id_chips = this.getArray(query["publisher_id"]);
        }

        if (query.hasOwnProperty("exclude_government")) {
          var excludedElse = this.getArray(query["exclude_government"]);
          if (this.territory_options && this.territory_options.length) {
            this.selected_territories = this.territory_options.map(t => t.value).filter(v => !excludedElse.includes(v));
          } else {
            this.injectExcludeGovernment = excludedElse;
          }
        } else if (this.getArray(query["government"]) && this.getArray(query["government"]).length > 0) {
          this.selected_territories = this.getArray(query["government"]);
        }

        if (query.hasOwnProperty("ispanel")) {
          this.ispanel = query["ispanel"]
        }
        if (query.hasOwnProperty("exclude_panel")) {
          this.exclude_panel = query["exclude_panel"]
        }
        if (query.hasOwnProperty("and_query")) {
          this.and_query = query["and_query"]
        }
        if (query.hasOwnProperty("exclude_grant")) {
          this.exclude_grant = query["exclude_grant"]
        }
        if (query.hasOwnProperty("isconsultancy")) {
          this.isconsultancy = query["isconsultancy"]
        }
        if (query.hasOwnProperty("isparent")) {
          this.isparent = query["isparent"]
        }
        if (query.hasOwnProperty("isindigenous")) {
          this.isindigenous = query["isindigenous"]
        }

        this.request_chips = this.getArray(query["recid"]);
        this.panel_chips = this.getArray(query["son_id"]);
        /* this.ispanel = query["ispanel"];
        this.exclude_panel = query["exclude_panel"];
        this.and_query = query["and_query"];
        this.exclude_grant = query["exclude_grant"];
        this.isconsultancy = query["isconsultancy"];
        this.isparent = query["isparent"]; */
      }

      this.emitFiltersChanged();
    },

    getArray(string) {
      if (Array.isArray(string)) {
        return string;
      } else {
        if (string > "") {
          let arr = new Array();
          arr[0] = string;
          return arr;
        } else {
          return string;
        }
      }
    },

    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split("-");
      return `${year}/${month}/${day}`;
    },

    remove_white_space(str) {
      return str.replace(/\s/g, "");
    },

    reset_filters() {

      //this.amount_index = false;
      this.keyword_chips = [];
      this.and_query = false;
      this.exclude_panel = false;
      this.exclude_grant = false;
      this.isparent = false;
      this.isindigenous = false;
      this.category_chips = [];
      this.tendle_cat_id_chips = [];
      this.agency_name_chips = [];
      this.agency_name_chips_exclude = [];
      this.supp_smart_tags = false;
      this.supplier_name_chips = [];
      this.supplier_smart_tags = false;
      this.supplier_description = false;
      this.supplier_industry_chips = [];
      this.pending_supplier_industries = [];
      this.supplier_group_name_chips = [];
      this.supplier_name_chips_exclude = [];
      this.supplier_id_chips = [];
      this.tendle_supp_id_chips = [];
      this.tendle_pub_id_chips = [];
      this.selected_statuses = [];
      this.selected_types = [];
      this.selected_publisher_industry = [];
      this.selected_industry_ids = [];
      this.selected_procurement_method = [];
      this.isconsultancy = false;
      this.selected_category_list = [];
      this.selected_exclude_category_list = [];
      this.selected_months = [];
      this.selected_parent_categories = [];
      this.request_chips = [];
      this.panel_chips = [];
      this.is_panel_off();
      this.published_range = [];
      this.closing_range = [];
      this.contract_start_range = [];
      this.contract_end_range = [];
      this.injectSuppClusters = [];
      this.injectPubClusters = [];
      this.selected_territories = [];
      this.injectExcludeGovernment = [];
      this.selected_contact = []
      //this.injectSuppClusters = [];
      //this.injectPubClusters = [];
      this.setAmountRange();
      this.clear_years();
      this.timeframeResetKey += 1;
    },

    get_acronym(str) {
      var matches = str.match(/\b(\w)/g); // ['J','S','O','N']
      var acronym = matches.join(""); // JSON
      return acronym;
    },

    getContacts(term) {
      fetchAllContacts(term).then(data => {
        this.loadingContacts = true;
        this.contact_list = data;
        this.loadingContacts = false;
      });
    },

    lookup(terms, field) {
      // Lazily load input items
      this.isLoading = true;
      var result;
      autocomplete(terms, field)
        .then(data => {
          //////////////////////////////console.log("DATA: ", data);
          data.unshift({ key: terms });
          if (field === "category") this.cat_items = data;
          if (field === "unique_publisher_name") this.pub_items = data;
          //if (field === "exclude_publisher") this.exclude_pub_items = data;
          if (field === "supplier") this.supp_items = data;
          if (field === "supplier_group_name") this.supplier_group_items = data;
          if (field === "keyword") this.supp_smart_tag_items = data;
        })
        .finally(() => (this.isLoading = false));
    },

    lookup_exclude(terms, field) {
      // Lazily load input items
      this.isLoading = true;
      var result;
      autocomplete(terms, field)
        .then(data => {
          //if (field === "category") this.cat_items = data;
          //if (field === "publisher") this.pub_items = data;
          data.unshift({ key: terms });
          if (field === "supplier") this.exclude_supp_items = data;
          if (field === "unique_publisher_name") this.exclude_pub_items = data;
          //if (field === "supplier") this.supp_items = data;
        })
        .finally(() => (this.isLoading = false));
    },

    normalizeTerritoryOptions(govTypes) {
      if (!govTypes || !Array.isArray(govTypes)) return [];
      return govTypes.map(gov => {
        if (typeof gov === "string") {
          return { value: gov };
        }
        if (gov && typeof gov === "object") {
          const value = gov.value || gov.text || gov.name || String(gov);
          return { value };
        }
        return { value: String(gov) };
      });
    },

    loadFilterOptions() {
      this.loadingGovts = true;
      fetchGovTypes()
        .then(results => {
          if (!results || !results.gov_types) {
            console.error("Failed to load government types:", results);
            return;
          }
          this.territory_options = this.normalizeTerritoryOptions(results.gov_types);
          if (this.injectExcludeGovernment && this.injectExcludeGovernment.length) {
            this.selected_territories = this.territory_options
              .map(t => t.value)
              .filter(v => !this.injectExcludeGovernment.includes(v));
            this.injectExcludeGovernment = [];
          }
        })
        .catch(error => {
          console.error("Error loading government types:", error);
        })
        .finally(() => {
          this.loadingGovts = false;
        });

      fetchCofogClassifications().then(data => {
        this.loadingIndustries = true;
        const classifications = data && data.data ? data.data : [];
        const flatMap = {};
        classifications.forEach(parent => {
          flatMap[parent.id] = parent;
          if (parent.children && parent.children.length) {
            parent.children.forEach(child => {
              flatMap[child.id] = child;
            });
          }
        });
        this.cofog_classifications = classifications;
        this.cofog_flat_map = flatMap;
        this.loadingIndustries = false;
      });

      fetchIndustries()
        .then(data => {
          if (data && Array.isArray(data)) {
            this.industry_options = data;
            this.industry_options_formatted = this.formatIndustriesForCombobox(data);
            this.supplierIndustryOptions = this.industry_options_formatted;

            // Re-hydrate supplier industry chips using the loaded options
            if (this.pending_supplier_industries.length > 0) {
              this.supplier_industry_chips = this.buildSupplierIndustryChips(this.pending_supplier_industries);
            }
          } else {
            console.error("Invalid industries data:", data);
          }
        })
        .catch(error => {
          console.error("Error loading industries:", error);
        });

      fetchProcurementMethods().then(data => {
        this.loadingProcurementMethods = true;
        this.procurement_method_list = data;
        this.loadingProcurementMethods = false;
      });

      fetchCategoryList().then(data => {
        this.loadingCategoryLists = true;
        this.category_list = data;
        this.loadingCategoryLists = false;
      });

      getParentCategories().then(data => {
        this.parent_category_options = data;
      });
    },

    /** Run callback once Auth0 has finished loading (mirrors authGuard.js). */
    whenAuthReady(fn) {
      const auth = this.$auth || getInstance();
      if (!auth) {
        console.error("Auth service not available");
        return;
      }
      if (!auth.loading) {
        fn();
        return;
      }
      const unwatch = auth.$watch("loading", loading => {
        if (!loading) {
          if (typeof unwatch === "function") unwatch();
          fn();
        }
      });
    },

    initTenderFilters() {
      this.whenAuthReady(() => {
        const auth = this.$auth || getInstance();
        if (!auth?.isAuthenticated) return;
        if (this.tenderFiltersInitialized) return;
        this.tenderFiltersInitialized = true;
        this.loadFilterOptions();
        this.populateFilters(this.$route.query);
      });
    },

  },
  mounted() {
    this.initTenderFilters();
  },

  beforeDestroy() {
    if (this._filtersChangedTimer != null) {
      clearTimeout(this._filtersChangedTimer);
      this._filtersChangedTimer = null;
    }
  },
  computed: {
    hasAdvancedFilters() {
      return this.getAdvancedFilterCount() > 0;
    },

    advancedFilterCount() {
      return this.getAdvancedFilterCount();
    },

    searchHeaderSnapshot() {
      return JSON.stringify({
        keywordKeys: (this.keyword_chips || []).map((chip) => chip && chip.key),
        advancedFilterCount: this.advancedFilterCount,
      });
    },

    publishedDateRangeText() {
      return this.published_range.join(" ~ ");
    },
    closingDateRangeText() {
      return this.closing_range.join(" ~ ");
    },
    contractStartRangeText() {
      return this.contract_start_range.join(" ~ ");
    },
    contractEndRangeText() {
      return this.contract_end_range.join(" ~ ");
    }
  },
  watch: {
    searchHeaderSnapshot() {
      this.emitFiltersChanged();
    },

    selected_industry_ids(newIds) {
      this.selected_publisher_industry = newIds
        .map(id => this.cofog_flat_map[id])
        .filter(Boolean);
      this.industry_tree_search = "";
    },

    $route(to) {
      if (!this.syncRouteOnNavigate) return;
      this.populateFilters(to.query);
    },

    $govtsLoaded(val) {
      //////////////////console.log("GOVT CHANGEDS: ");
      this.territory_options = val;
      if (this.injectExcludeGovernment && this.injectExcludeGovernment.length) {
        this.selected_territories = this.territory_options.map(t => t.value).filter(v => !this.injectExcludeGovernment.includes(v));
        this.injectExcludeGovernment = [];
      }
    },

    $buyer_industries: {
      handler() {
        //////////////////console.log("INDUSRTY CHANGEDS: ");
        this.getIndustries();
      },
      deep: true
    },

    cat_search(val) {
      val && val !== this.select && this.lookup(val, "category");
    },
    pub_search(val) {
      if (val && val !== this.select) {
        this.pub_items = this.lookup(val, "unique_publisher_name");
      }
    },
    exclude_pub_search(val) {
      if (val && val !== this.select) {
        this.exclude_pub_items = this.lookup_exclude(val, "unique_publisher_name");
      }
    },
    supp_search(val) {
      val && val !== this.select && this.lookup(val, "supplier");
    },
    supplier_group_search(val) {
      val && val !== this.select && this.lookup(val, "supplier_group_name");
    },
    supp_smart_tags(val) {
      val && val !== this.select && this.lookup(val, "keyword");
    },
    search_contacts(val) {
      val && val !== this.select && this.getContacts(val);
    },
    exclude_supp_search(val) {
      if (val && val !== this.select) {
        this.exclude_supp_items = this.lookup_exclude(val, "supplier");
      }
    }
  }
};
</script>
<style scoped>
.tender-filters-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.tender-filters-layout__header {
  flex-shrink: 0;
}

.tender-filters-layout__help-sup {
  line-height: 0;
  margin-left: 6px;
}

.tender-filters-layout__help-sup a {
  text-decoration: none;
  opacity: 0.85;
}

.tender-filters-layout__help-sup a:hover {
  opacity: 1;
}

.tender-filters-layout__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 0;
}

.tender-filters-layout__footer {
  flex-shrink: 0;
  width: 100%;
  margin-top: 0;
  margin-bottom: 0;
  padding: 12px 16px 16px;
  background-color: #12253a;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.chip-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.dark-badge-text {
  color: #12253a;
  /* Or any dark color you prefer */
}

.industry-tree-scroll {
  max-height: 280px;
  overflow-y: auto;
}

.industry-tree-activator .v-input {
  margin-bottom: 0 !important;
}

/* Pink wash on panel header when that section has active filters */
.tender-filters__panel--highlighted >>> .tender-filters__panel-header,
.tender-filters__panel--highlighted >>> .tender-filters__panel-header.v-expansion-panel-header--active,
.tender-filters__panel--highlighted >>> .tender-filters__panel-header.primary--text {
  background: rgba(255, 20, 99, 0.18) !important;
  color: rgba(255, 255, 255, 0.75) !important;
}

.tender-filters__panel--highlighted >>> .tender-filters__panel-header .v-icon,
.tender-filters__panel--highlighted >>> .tender-filters__panel-header.v-expansion-panel-header--active .v-icon {
  color: rgba(255, 255, 255, 0.75) !important;
}
</style>

<style>
.industry-tree-menu {
  min-width: 280px !important;
  max-width: 320px !important;
  background-color: #ffffff !important;
  border-radius: 0 0 4px 4px !important;
  box-shadow: 0 4px 8px rgba(0,0,0,0.2) !important;
  margin-top: 0 !important;
}
</style>